<script setup>
/* eslint-disable */

import { ref, computed, watch, nextTick } from 'vue'
import { useGameStore } from '@/store/game'
import { TRAINING_TYPES } from '@/models/chocobos'
import { NPCOwnerManager } from '@/services/npcOwnerManager'
import ranchHandImage from '@/assets/ranch_hand.jpg'
import { RaceSimulator } from '@/raceLogic'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'
import RaceVisualization from './RaceVisualization.vue'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'
import { MonthlyRaceManager } from '@/services/monthlyRaceManager'

const props = defineProps({
  results: {
    type: Object,
    required: true
  },
  withVisualization: {
    type: Boolean,
    default: false
  }
})


const formatFinishTime = (time) => {
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  const milliseconds = Math.floor((time % 1) * 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
}


const emit = defineEmits(['close', 'view-offer', 'view-auctions'])
const gameStore = useGameStore()

const processedResults = computed(() => {
  if (!props.results?.results) return []
  
  
  return props.results.results.map(result => ({
    ...result,
    formattedTime: formatFinishTime(result.finishTime)
  }))
})


const showingVisualization = ref(props.withVisualization)
const npcNotifications = ref([])
const showingNotifications = ref(false)
const currentNotificationIndex = ref(0)
const resultsProcessed = ref(false)

const earningsSummary = computed(() => {
  if (!processedResults.value || processedResults.value.length === 0) return null

  const playerResult = processedResults.value.find(result => 
    isPlayerChocobo(result.chocobo)
  )
  
  if (!playerResult) return null

  const entryFee = props.results.circuit.entryFee
  const raceEarnings = playerResult.moneyReward
  
  // Calculate jockey fee based on placement and grade
  const jockeyGrade = playerResult.jockey.skillGrade
  const jockeyFees = RaceSimulator.JOCKEY_FEES[jockeyGrade]
  const jockeyPayment = raceEarnings > 0 
    ? Math.round(raceEarnings * jockeyFees.winningCut)
    : jockeyFees.flatFee

  const netEarnings = raceEarnings - entryFee - jockeyPayment

  return {
    entryFee,
    raceEarnings,
    jockeyPayment,
    netEarnings
  }
})

function processRaceExperience() {
  const results = props.results.results.map(result => {
    const chocobo = result.chocobo;
    const improvements = chocobo.gainExperience(result.experienceGained);
    
    return {
      ...result,
      experienceGained: result.experienceGained,
      levelUp: improvements ? {
        oldLevel: improvements.oldLevel,
        newLevel: improvements.newLevel,
        speedGain: improvements.speedGain,
        staminaGain: improvements.staminaGain,
        behaviorImproved: improvements.behaviorImproved,
        trainingFocus: improvements.trainingFocus
      } : null
    };
  });

  // Store notifications but don't show them yet
  NPCOwnerManager.processTrainingDecisions(gameStore);
  npcNotifications.value = NPCOwnerManager.processAuctionAndSales(gameStore);
  
  // Mark results as processed
  resultsProcessed.value = true;
  
  return results;
}


const handleNotificationNext = () => {
  if (currentNotificationIndex.value < npcNotifications.value.length - 1) {
    currentNotificationIndex.value++
  } else {
    showingNotifications.value = false
    currentNotificationIndex.value = 0
    emit('close')
  }
}

const handleViewOffer = (bid) => {
  if (!bid || !bid.ownerId) return;
  
  currentBid.value = {
    amount: bid.amount,
    owner: gameStore.owners.find(owner => owner.id === bid.ownerId),
    chocobo: gameStore.chocobos.find(chocobo => chocobo.id === bid.chocoboId)
  };
  
  
  if (currentBid.value?.owner && currentBid.value?.chocobo) {
    showOfferModal.value = true;
  }
  
  showRaceResults.value = false;
};


const currentNotification = computed(() => {
  if (!npcNotifications.value.length) return null
  return npcNotifications.value[currentNotificationIndex.value]
})

const getPositionText = (position) => {
  if (position === 1) return '1st';
  if (position === 2) return '2nd';
  if (position === 3) return '3rd';
  if (position === 11) return '11th';
  if (position === 12) return '12th';
  if (position === 13) return '13th';
  
  // Handle all other numbers
  const lastDigit = position % 10;
  switch (lastDigit) {
    case 1: return `${position}st`;
    case 2: return `${position}nd`;
    case 3: return `${position}rd`;
    default: return `${position}th`;
  }
}

// You can also have it work with the getPositionTheme function:
const getPositionTheme = (position) => {
  switch(position) {
    case 1: return 'first';
    case 2: return 'second';
    case 3: return 'third';
    default: return 'other';
  }
}

const stableBoyAdvice = computed(() => {
  if (!processedResults.value || processedResults.value.length === 0) return null

  const playerResult = processedResults.value.find(result => 
    isPlayerChocobo(result.chocobo)
  )
  
  if (!playerResult) return null

  if (playerResult.position === 1) {
    // Check if this is already a Gold Saucer race
    if (props.results.circuit.id === 'saucer') {
      return "That's our bird! Beautiful racing today. A win at the Saucer - it doesn't get better than this!"
    }
    return "That's our bird! Beautiful racing today. Keep this up and we'll be winning at Gold Saucer in no time!"
  }

  if (playerResult.chocobo.behaviorId >= 4 && playerResult.position >= 4) {
    return "We've got to get this birds behaviour under control, it almost threw our Jock today!"
  }

  const totalWeight = playerResult.chocobo.stamina * 0.6 + playerResult.chocobo.speed * 0.4
  const weightDiff = Math.abs(playerResult.jockey.weight - totalWeight)
  const skillGradeValue = {
    'S': 6, 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1, 'F': 0
  }[playerResult.jockey.skillGrade]

  if (weightDiff > 20) {
    if (props.results.raceType === 'short' && playerResult.jockey.weight > totalWeight) {
      return "Try a lighter jockey with this bird in the next sprint. We were carrying too much weight out there."
    }
    if (props.results.raceType === 'long' && playerResult.jockey.weight < totalWeight) {
      return "I think the jockey was a little shy of weight, we struggled with grip in the longer stretches."
    }
  }

  if (skillGradeValue < 4) {
    return "This jockey got our bird beaten today. We might want to look for someone with more experience."
  }

  const winnerLevel = processedResults.value[0].chocobo.level
  if (winnerLevel - playerResult.chocobo.level > 5) {
    return "We should be aiming for a lesser class of opponent with this bird."
  }

  return "We'll get 'em next time! Keep working on training and we'll see better results."
})

const getSkillGradeClass = (grade) => {
  const classes = {
    'S': 'bg-purple-100 text-purple-800',
    'A': 'bg-blue-100 text-blue-800',
    'B': 'bg-green-100 text-green-800',
    'C': 'bg-yellow-100 text-yellow-800',
    'D': 'bg-orange-100 text-orange-800',
    'E': 'bg-red-100 text-red-800',
    'F': 'bg-gray-100 text-gray-800'
  }
  return classes[grade] || classes['F']
}

const getPositionClass = (position) => {
  switch(position) {
    case 1: return 'bg-yellow-100 border-yellow-500 text-yellow-800'
    case 2: return 'bg-gray-100 border-gray-500 text-gray-800'
    case 3: return 'bg-amber-100 border-amber-700 text-amber-800'
    default: return 'bg-white border-gray-200 text-gray-600'
  }
}

const getPositionBadgeClass = (position) => {
  switch(position) {
    case 1: return 'bg-yellow-500 text-white'
    case 2: return 'bg-gray-500 text-white'
    case 3: return 'bg-amber-700 text-white'
    default: return 'bg-gray-200 text-gray-600'
  }
}

const isPlayerChocobo = (chocobo) => {
  return gameStore.playerOwner.chocoboIds.includes(chocobo.id)
}

const getTrainingFocusIcon = (trainingType) => {
  switch(trainingType) {
    case 'speed':
      return '🏃'
    case 'stamina':
      return '💪'
    case 'behavior':
      return '🎯'
    default:
      return '🏃'
  }
}

const getOwnerName = (chocobo) => {
  const owner = gameStore.owners.find(owner => 
    owner.chocoboIds.includes(chocobo.id)
  )
  return owner ? owner.name : 'Unknown'
}

const isPotentialMonthlyBest = (result) => {
  if (!result || !result.chocobo || !isPlayerChocobo(result.chocobo)) return false
  
  const currentMonth = MonthlyRaceManager.getMonthFromDay(gameStore.currentDay)
  const bestTimes = MonthlyRaceManager.getMonthlyBestTimes(gameStore.chocobos, currentMonth)
  
  if (!bestTimes || !bestTimes[props.results.raceType]) return false
  
  const currentBest = bestTimes[props.results.raceType]
  const resultNormalizedTime = MonthlyRaceManager.normalizeTime(
    result.finishTime, 
    result.chocobo, 
    props.results.raceType
  )
  
  // Check if this result would beat the current best
  return resultNormalizedTime < currentBest.normalized || currentBest.chocobo === null
}

watch(() => props.results, (newResults) => {
  if (!showingVisualization.value && newResults) {
    nextTick(() => {
      processedResults.value = processRaceExperience()
    })
  }
}, { immediate: true })

</script>
<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6`">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 :class="applyThemeClasses('text.primary')">Race Results</h2>
        <button 
          @click="emit('close')"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      <div v-if="earningsSummary" class="mb-6 rounded-lg p-4">
        <h3 :class="applyThemeClasses('text.primaryLow')">Race Summary</h3>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <div :class="`${applyThemeClasses('text.secondaryBold')}`">Prize Money</div>
            <div :class="`${applyThemeClasses('text.money')}`">
              {{ earningsSummary.raceEarnings > 0 
                ? `+${earningsSummary.raceEarnings.toLocaleString()}G` 
                : '0G' }}
            </div>
          </div>
          <div>
            <div :class="`${applyThemeClasses('text.secondaryBold')}`">Entry Fee</div>
            <div class="font-medium text-red-600">
              -{{ earningsSummary.entryFee.toLocaleString() }}G
            </div>
          </div>
          <div>
            <div :class="`${applyThemeClasses('text.secondaryBold')}`">Jockey Payment</div>
            <div class="font-medium text-red-600">
              -{{ earningsSummary.jockeyPayment.toLocaleString() }}G
            </div>
          </div>
          <div>
            <div :class="`${applyThemeClasses('text.secondaryBold')}`">Net Earnings</div>
            <div 
              :class="[
                'font-medium',
                earningsSummary.netEarnings >= 0 ? 'text-green-600' : 'text-red-600'
              ]"
            >
              {{ earningsSummary.netEarnings >= 0 ? '+' : '' }}{{ earningsSummary.netEarnings.toLocaleString() }}G
            </div>
          </div>
        </div>
      </div>
      <div v-if="stableBoyAdvice" :class="`${applyThemeClasses('race.advice.container')} p-4`">
        <div class="flex items-start space-x-4">
          <img 
            :src="ranchHandImage" 
            alt="Stable Boy" 
            class="w-16 h-16 rounded-lg object-cover"
          />
          <div class="flex-1">
            <p :class="applyThemeClasses('race.advice.text')">
              "{{ stableBoyAdvice }}"
            </p>
          </div>
        </div>
      </div>
      <!-- Race Results List -->
      <div class="space-y-4">
        <div 
          v-for="result in processedResults" 
          :key="`${result.chocobo.id}-${result.jockey.id}`"
          :class="[
            'border-2 rounded-lg overflow-hidden transition-all',
            applyThemeClasses(`race.position.${getPositionTheme(result.position)}.bg`),
            isPlayerChocobo(result.chocobo) && !isPotentialMonthlyBest(result) ? 'transform scale-105' : '',
            isPotentialMonthlyBest(result) ? 'transform scale-105 border-4 border-yellow-400 shadow-lg shadow-yellow-300/50' : ''
          ]"
        >
          <div class="flex items-center p-4">
            <!-- Position Badge -->
            <div 
              :class="[
                'w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mr-4',
                'transform transition-all duration-200',
                applyThemeClasses(`race.position.${getPositionTheme(result.position)}.badge`),
                result.position <= 3 ? 'scale-110' : ''
              ]"
            >
              {{ getPositionText(result.position) }}
            </div>

            <!-- Chocobo Info -->
            <div class="flex-grow">
              <div class="flex items-center space-x-4">
                <!-- Chocobo Color Badge -->
                <div 
                  :class="[
                    getChocoboColorClass(result.chocobo.color),
                    'w-3 h-3 rounded-full'
                  ]"
                ></div>
                
                <!-- Names and Details -->
                <div class="flex-1 min-w-0">
                  <h3 class="font-bold text-lg flex items-center gap-2">
                    <span :class="applyThemeClasses('text.primary')">
                      {{ result.chocobo.name }}
                    </span>
                    <span 
                      v-if="isPlayerChocobo(result.chocobo)" 
                      :class="applyThemeClasses('race.playerTag')"
                      class="inline-block text-xs px-2 py-1 rounded-full"
                    >
                      Your Chocobo
                    </span>
                  </h3>
                  <div :class="applyThemeClasses('text.secondary')" class="space-y-1 text-sm">
                    <div>Owner: {{ getOwnerName(result.chocobo) }}</div>
                    <div>Jockey: {{ result.jockey.name }}</div>
                    <div class="flex items-center gap-2">
                      <span 
                        :class="[
                          'px-2 py-0.5 rounded text-xs font-medium',
                          getSkillGradeClass(result.jockey.skillGrade)
                        ]"
                      >
                        {{ result.jockey.skillGrade }} Class
                      </span>
                      <span :class="applyThemeClasses('text.secondary')">
                        {{ result.jockey.weightCategory }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Stats -->
                <div class="ml-auto text-right shrink-0">
                  <div class="grid grid-cols-2 gap-2">
                    <!-- Speed Stat -->
                    <div class="flex flex-col items-center rounded p-2">
                      <span :class="applyThemeClasses('text.secondary')" class="text-xs mb-1">
                        SPD
                      </span>
                      <div 
                        class="w-6 h-6 flex items-center justify-center rounded-full"
                        :class="getStatGradeClass(getStatGrade(result.chocobo.speed))"
                      >
                        <span class="font-bold text-lg">
                          {{ getStatGrade(result.chocobo.speed) }}
                        </span>
                      </div>
                    </div>

                    <!-- Stamina Stat -->
                    <div class="flex flex-col items-center rounded p-2">
                      <span :class="applyThemeClasses('text.secondary')" class="text-xs mb-1">
                        STA
                      </span>
                      <div 
                        class="w-6 h-6 flex items-center justify-center rounded-full"
                        :class="getStatGradeClass(getStatGrade(result.chocobo.stamina))"
                      >
                        <span class="font-bold text-lg">
                          {{ getStatGrade(result.chocobo.stamina) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Level Up Section -->
          <div 
            v-if="result.levelUp"
            :class="applyThemeClasses('race.levelUp.container')"
            class="p-4"
          >
            <div class="flex items-start space-x-4">
              <div class="text-2xl">⭐</div>
              <div class="flex-grow">
                <h4 :class="applyThemeClasses('race.levelUp.title')" class="font-bold">
                  Level Up! {{ result.levelUp.oldLevel }} → {{ result.levelUp.newLevel }}
                </h4>
                <div class="space-y-1 mt-2">
                  <p class="text-sm flex items-center gap-2">
                    <span :class="applyThemeClasses('text.secondary')">
                      Training Focus: {{ getTrainingFocusIcon(result.levelUp.trainingFocus) }}
                    </span>
                  </p>
                  <p 
                    v-if="result.levelUp.speedGain > 0" 
                    class="text-sm"
                    :class="applyThemeClasses('race.levelUp.stat.speed')"
                  >
                    Speed +{{ result.levelUp.speedGain }}
                  </p>
                  <p 
                    v-if="result.levelUp.staminaGain > 0" 
                    class="text-sm"
                    :class="applyThemeClasses('race.levelUp.stat.stamina')"
                  >
                    Stamina +{{ result.levelUp.staminaGain }}
                  </p>
                  <p 
                    v-if="result.levelUp.behaviorImproved" 
                    class="text-sm"
                    :class="applyThemeClasses('race.levelUp.stat.behavior')"
                  >
                    Behavior improved to: {{ result.chocobo.behavior.description }}
                  </p>
                  <p 
                    v-if="result.levelUp.trainingFocus === TRAINING_TYPES.BEHAVIOR && !result.levelUp.behaviorImproved" 
                    :class="applyThemeClasses('text.secondary')"
                    class="text-sm"
                  >
                    Behavior training progress: {{ result.chocobo.consecutiveBehaviorTraining }}/6
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Time Display -->
          <div 
            :class="[
              applyThemeClasses('race.stats.container'),
              isPotentialMonthlyBest(result) ? 'bg-yellow-100 border-2 border-yellow-400' : ''
            ]"
            class="p-2 text-sm"
          >
            <div class="flex items-center justify-between">
              <div>
                <span :class="applyThemeClasses('race.stats.text')" class="font-medium">
                  Finish Time:
                </span>
                <span :class="applyThemeClasses('race.stats.text')">
                  {{ result.formattedTime }}
                </span>
              </div>
              <div v-if="isPotentialMonthlyBest(result)" class="flex items-center gap-2">
                <span class="text-yellow-600 font-bold">🏆 Monthly Best!</span>
                <span class="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full">
                  Prize at month end
                </span>
              </div>
            </div>
          </div>

          <!-- Experience Gain -->
          <div 
            v-if="!result.levelUp" 
            :class="applyThemeClasses('race.stats.container')"
            class="p-2 text-sm"
          >
            <span :class="applyThemeClasses('race.stats.text')">
              Experience gained: +{{ result.experienceGained }}
            </span>
          </div>
        </div>
      </div>

      <!-- NPC Notifications Overlay -->
      <div 
        v-if="showingNotifications" 
        :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-[60]`"
      >
        <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} p-6 max-w-2xl w-full`">
          <div class="flex items-start space-x-4 mb-6">
            <img 
              :src="ranchHandImage" 
              alt="Ranch Hand" 
              class="w-16 h-16 rounded-lg object-cover"
            />
            <div class="flex-1">
              <h3 :class="applyThemeClasses('text.primary')">Ranch Hand Update</h3>
              <p :class="applyThemeClasses('text.secondary')">
                {{ currentNotification?.message }}
              </p>

              <!-- Bid Details -->
              <div v-if="currentNotification?.type === 'bid'"
                  class="mt-4 p-4 bg-blue-50 rounded-lg">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="font-medium">Offer Amount:</span>
                    <span class="text-green-600 ml-2">
                      {{ currentNotification.bid.amount.toLocaleString() }}G
                    </span>
                  </div>
                  <button
                    @click="$emit('view-offer', currentNotification.bid)"
                    class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    View Offer
                  </button>
                </div>
              </div>

              <!-- Auction Details -->
              <div 
                v-if="currentNotification?.type === 'auctions'"
                class="mt-4 p-4 bg-amber-50 rounded-lg"
              >
                <div class="flex items-center justify-between">
                  <div class="text-amber-800">
                    {{ currentNotification.auctions.length }} new auction listings available
                  </div>
                  <button
                    class="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                    @click="$emit('view-auctions')"
                  >
                    View Auctions
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center">
            <div :class="applyThemeClasses('text.secondary')">
              {{ currentNotificationIndex + 1 }} of {{ npcNotifications.length }}
            </div>
            <button 
              @click="handleNotificationNext"
              class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {{ currentNotificationIndex < npcNotifications.length - 1 ? 'Next' : 'Done' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-6 flex justify-end">
        <div class="mt-6 flex justify-end space-x-4">
          <button 
            v-if="npcNotifications.length > 0"
            @click="showingNotifications = true"
            class="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
          >
            <span>Notifications & Continue</span>
            <span class="bg-amber-400 text-amber-800 text-sm px-2 py-0.5 rounded-full">
              {{ npcNotifications.length }}
            </span>
          </button>
          <button 
            @click="emit('close')"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>